import { useSites } from '../../hooks/useSites';
import { useEmailSubscriptions } from '../../hooks/useEmailSubscriptions';

export function EmailSubscriptionsRow() {
  const sitesQuery = useSites();
  const sites = sitesQuery.data?.sites ?? [];
  const { subscriptions, isLoading, isToggling, error, toggle } = useEmailSubscriptions();

  if (sites.length === 0) return null;

  return (
    <div className="bg-ems-bg-subtle border border-ems-border rounded-md p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-ems-text-muted mb-2">
        Email Subscriptions
      </div>
      {error && (
        <div className="text-xs text-red-600 mb-2" role="alert">
          Couldn't save subscription. Please try again. ({error})
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {isLoading
          ? Array.from({ length: Math.max(sites.length, 1) }).map((_, i) => (
              <div
                key={i}
                data-testid="subscription-pill-skeleton"
                className="h-7 w-24 bg-gray-200 rounded-full animate-pulse"
              />
            ))
          : sites.map((site) => {
              const sub = subscriptions.find((s) => s.site_id === site.site_id);
              const enabled = sub?.enabled ?? false;
              return (
                <SubscriptionPill
                  key={site.site_id}
                  siteName={site.name}
                  enabled={enabled}
                  disabled={isToggling}
                  onClick={() => toggle(site.site_id, !enabled)}
                />
              );
            })}
      </div>
    </div>
  );
}

interface SubscriptionPillProps {
  siteName: string;
  enabled: boolean;
  disabled: boolean;
  onClick: () => void;
}

function SubscriptionPill({ siteName, enabled, disabled, onClick }: SubscriptionPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={enabled}
      className={[
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors',
        enabled
          ? 'bg-blue-50 border-blue-600 text-blue-800 font-semibold'
          : 'bg-white border-gray-300 text-gray-600',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-100',
      ].join(' ')}
    >
      <span>{siteName}</span>
      <span aria-hidden>{enabled ? '✓' : '○'}</span>
    </button>
  );
}
